<template>
  <form @submit.prevent="saveAppliance" ref="editApplianceForm">
    <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="editApplianceModal" @keyup.esc="onEsc($event)">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header header">
            <h3 class="modal-title">{{ action === 'edit' ? $t('editApplianceModal.titleEdit') : $t('editApplianceModal.titleAdd') }}</h3>
            <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form">
              <div v-show="errorMessage" class="alert alert-danger failed">{{ errorMessage }}</div>
              <div v-if="parentFacility" class="form-group">
                <label>{{ $t('editApplianceModal.facilityLabel') }}</label>
                <div class="blue-text">{{ parentFacility.name }}</div>
              </div>
              <div class="form-group">
                <label for="applianceNameInput">{{ $t('editApplianceModal.nameInput') }}</label>
                <input type="text" class="form-control" :class="{ 'is-invalid': v$.form.name.$dirty && v$.form.name.$error }" id="applianceNameInput" v-model.trim="form.name" :placeholder="$t('editApplianceModal.namePlaceholder')" maxlength="50" />
                <div class="invalid-feedback" style="display: block;" v-if="v$.form.name.$dirty && v$.form.name.$error">
                  <div class="error" v-if="v$.form.name.required.$invalid">{{ $t('editApplianceModal.nameRequired') }}</div>
                </div>
              </div>
              <div class="form-group">
                <label for="applianceIconSelect">{{ $t('editApplianceModal.iconInput') }}</label>
                <div class="icon-row">
                  <span class="icon-preview"><font-awesome-icon :icon="previewIcon" /></span>
                  <select class="form-control" id="applianceIconSelect" v-model="form.icon">
                    <option value="">{{ $t('editApplianceModal.iconNone') }}</option>
                    <option v-for="entry in iconChoices" :value="entry.key" :key="entry.key">{{ iconLabel(entry.key) }}</option>
                  </select>
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('editApplianceModal.categoryLabel') }}</label>
                <div class="form-control-plaintext">{{ categoryLabel }}</div>
              </div>
              <div class="form-group">
                <label>{{ $t('editApplianceModal.pointsLabel') }}</label>
                <div class="small text-muted">{{ $t('editApplianceModal.pointsHint', { sensor: currentSensor ? currentSensor.name : '' }) }}</div>
                <div v-for="point in sensorPoints" :key="point.systempart" class="form-check">
                  <input class="form-check-input" type="checkbox" :id="'appliancePoint' + point.systempart" :value="point.systempart" v-model="form.systemparts">
                  <label class="form-check-label" :for="'appliancePoint' + point.systempart">{{ point.label }}</label>
                </div>
                <div class="small text-muted" v-if="otherSensorPointCount > 0">{{ $t('editApplianceModal.otherPoints', { count: otherSensorPointCount }) }}</div>
                <div class="invalid-feedback" style="display: block;" v-if="showPointsRequired">
                  {{ $t('editApplianceModal.pointsRequired') }}
                </div>
              </div>
              <div class="form-group">
                <label for="applianceNoteInput">{{ $t('editApplianceModal.noteInput') }}</label>
                <input type="text" class="form-control" id="applianceNoteInput" v-model.trim="form.note" maxlength="250" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button v-if="action === 'edit'" type="button" class="btn btn-outline-danger me-auto" @click="requestDelete">
              <font-awesome-icon icon="trash" /> &nbsp; {{ $t('editApplianceModal.delete') }}
            </button>
            <button type="submit" class="btn btn-primary">{{ action === 'edit' ? $t('editApplianceModal.buttonSave') : $t('editApplianceModal.buttonAdd') }}</button>
            <button type="button" class="btn btn-secondary" @click="close">{{ $t('editApplianceModal.close') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { Modal } from 'bootstrap'
import { required, maxLength } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import appliancesService from '@/services/appliances'
import { iconsForType, faIconForAppliance, checkedSystempartsForSensor, mergePointsForSensor } from '@/utils/appliances'

//
// Verbraucher anlegen / bearbeiten aus dem Sensor-Detail heraus. Die
// Messpunkte dieses Sensors werden als Checkboxen je Systemteil angeboten;
// Punkte an anderen Sensoren bleiben beim Sichern erhalten
// (mergePointsForSensor). Die Kategorie leitet der Server aus dem
// Sensortyp ab und wird hier nur angezeigt.
//
export default {
  setup () { return { v$: useVuelidate() } },
  name: 'EditApplianceModal',
  props: {
    parentFacility: { type: Object, default: null },
    currentSensor: { type: Object, required: true },
    // [{ systempart, label }] offered as checkboxes
    sensorPoints: { type: Array, default: () => [] },
    // wire shape of the appliance when editing
    selectedAppliance: { type: Object, default: null },
    // systempart to tick on "add" (round trip from the observation modal)
    preselectSystempart: { type: Number, default: null },
    action: { type: String, default: 'add' },
    modalOpened: { type: String, required: true }
  },
  emits: ['modalClosed', 'deleteRequested'],
  data () {
    return {
      form: this.emptyForm(),
      showPointsRequired: false,
      errorMessage: '',
      anyChanges: false,
      savedAppliance: null
    }
  },
  validations: {
    form: {
      name: { required, maxLength: maxLength(50) }
    }
  },
  computed: {
    sensorType () {
      return this.currentSensor ? Number(this.currentSensor.type) : 0
    },
    iconChoices () {
      return iconsForType(this.sensorType)
    },
    previewIcon () {
      return faIconForAppliance({ icon: this.form.icon, category: this.sensorType })
    },
    categoryLabel () {
      return this.$t('sensorType.' + this.sensorType)
    },
    otherSensorPointCount () {
      if (!this.selectedAppliance || !this.currentSensor) return 0
      return (this.selectedAppliance.measurementpoints || []).filter(mp => Number(mp.sensorid) !== Number(this.currentSensor.id)).length
    }
  },
  watch: {
    modalOpened () {
      this.resetForm()
      this.$nextTick(() => {
        document.getElementById('applianceNameInput')?.focus()
      })
    }
  },
  methods: {
    emptyForm () {
      return { name: '', icon: '', note: '', systemparts: [] }
    },
    resetForm () {
      this.errorMessage = ''
      this.anyChanges = false
      this.savedAppliance = null
      this.showPointsRequired = false
      if (this.action === 'edit' && this.selectedAppliance) {
        this.form = {
          name: this.selectedAppliance.name || '',
          icon: this.selectedAppliance.icon || '',
          note: this.selectedAppliance.note || '',
          systemparts: checkedSystempartsForSensor(this.selectedAppliance, this.currentSensor ? this.currentSensor.id : null)
        }
      } else {
        this.form = this.emptyForm()
        if (this.preselectSystempart != null) {
          this.form.systemparts = [Number(this.preselectSystempart)]
        }
      }
      this.v$.$reset()
    },
    iconLabel (key) {
      const translated = this.$t('applianceIcon.' + key)
      return translated && translated !== 'applianceIcon.' + key ? translated : key
    },
    saveAppliance () {
      this.v$.$touch()
      const existingPoints = this.action === 'edit' && this.selectedAppliance ? this.selectedAppliance.measurementpoints : []
      const measurementpoints = mergePointsForSensor(existingPoints, this.currentSensor.id, this.form.systemparts)
      this.showPointsRequired = measurementpoints.length === 0
      if (this.v$.$invalid || this.showPointsRequired) {
        return
      }
      const detail = {
        name: this.form.name,
        icon: this.form.icon || '',
        note: this.form.note || '',
        measurementpoints
      }
      const request = (this.action === 'edit' && this.selectedAppliance)
        ? appliancesService.patchAppliance(this.selectedAppliance.id, detail)
        : appliancesService.addAppliance(this.parentFacility.id, detail)
      request.then((data) => {
        this.anyChanges = true
        this.savedAppliance = data && data.appliance ? data.appliance : null
        this.close()
      }).catch(error => {
        this.errorMessage = this.localizedBackendMessage(error.message)
      })
    },
    requestDelete () {
      const appliance = this.selectedAppliance
      this.hide()
      this.$emit('modalClosed', false)
      this.$emit('deleteRequested', appliance)
    },
    onEsc () {
      this.close()
    },
    hide () {
      Modal.getInstance(document.getElementById('editApplianceModal'))?.hide()
    },
    close () {
      this.$emit('modalClosed', this.anyChanges, this.savedAppliance)
      this.hide()
    },
    localizedBackendMessage (errorMessage) {
      return this.$t('backend.' + errorMessage)
    }
  }
}
</script>

<style lang="scss" scoped>
.modal {
  .modal-dialog {
    width: 480px;
  }
  .blue-text {
    color: #0069d9;
    font-weight: 700;
  }
  .icon-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .icon-preview {
      width: 2em;
      text-align: center;
      font-size: 1.3em;
    }
    select {
      flex: 1 1 auto;
    }
  }
}
</style>
